import { WW, WH, TS, SOLID, LIQUID, NONSOLID_PASS, PW, PH, T } from './constants';
import { MobRenderer } from './MobRenderer';

// === LIQUID SIM — Terraria-style water physics ===
const MIN_LIQUID = 0.005;
const MAX_LIQUID = 1.0;
const FLOW_RATE = 0.25;
const SIDE_FLOW_RATE = 0.12;
const LAVA_FLOW_MULT = 0.25;

export class LiquidSim {
    constructor(w) {
        this.w = w;
        this.t = 0;
        this.settled = new Uint8Array(WW * WH);
    }

    unsettle(x, y) {
        for (let dx = -2; dx <= 2; dx++) {
            for (let dy = -2; dy <= 2; dy++) {
                const nx = x + dx, ny = y + dy;
                if (nx >= 0 && nx < WW && ny >= 0 && ny < WH) {
                    this.settled[ny * WW + nx] = 0;
                }
            }
        }
    }

    tick(cx, cy, radius) {
        this.t++;
        // Run every frame for smoothness
        const mnX = Math.max(1, cx - radius);
        const mxX = Math.min(WW - 2, cx + radius);
        const mnY = Math.max(1, cy - radius);
        const mxY = Math.min(WH - 2, cy + radius);

        // Process bottom-to-top for correct gravity propagation
        for (let y = mxY; y >= mnY; y--) {
            // Alternate scan direction each frame to avoid directional bias
            const leftToRight = (this.t + y) % 2 === 0;
            const startX = leftToRight ? mnX : mxX;
            const endX = leftToRight ? mxX : mnX;
            const stepX = leftToRight ? 1 : -1;

            for (let x = startX; leftToRight ? x <= endX : x >= endX; x += stepX) {
                const t = this.w.gt(x, y);
                if (!LIQUID.has(t)) continue;

                const idx = y * WW + x;
                if (this.settled[idx]) continue;

                let lvl = this.w.gll(x, y);
                if (lvl <= 0) continue;

                const isLava = t === T.LAVA;
                const flowMult = isLava ? LAVA_FLOW_MULT : 1.0;
                // Lava moves slower
                if (isLava && this.t % 4 !== 0) continue;

                let remaining = lvl;
                let didFlow = false;

                // 1. Flow DOWN — gravity is primary
                const below = this.w.gt(x, y + 1);
                if (below === T.AIR || (LIQUID.has(below) && below === t)) {
                    const belowLvl = below === T.AIR ? 0 : this.w.gll(x, y + 1);
                    const space = MAX_LIQUID - belowLvl;
                    if (space > MIN_LIQUID) {
                        const flow = Math.min(remaining, space, Math.max(FLOW_RATE * flowMult, remaining * 0.5));
                        if (flow > MIN_LIQUID) {
                            remaining -= flow;
                            if (below === T.AIR) this.w.st(x, y + 1, t);
                            this.w.sll(x, y + 1, belowLvl + flow);
                            this.unsettle(x, y + 1);
                            didFlow = true;
                        }
                    }
                }

                // 2. Flow SIDEWAYS — equalize pressure
                if (remaining > MIN_LIQUID) {
                    for (const dx of [-1, 1]) {
                        const nx = x + dx;
                        if (nx < 0 || nx >= WW) continue;
                        const side = this.w.gt(nx, y);
                        if (SOLID.has(side)) continue;
                        if (side !== T.AIR && !(LIQUID.has(side) && side === t) && !NONSOLID_PASS.has(side)) continue;

                        const sideLvl = (side === T.AIR || NONSOLID_PASS.has(side)) ? 0 : this.w.gll(nx, y);
                        const diff = remaining - sideLvl;

                        if (diff > MIN_LIQUID * 2) {
                            // Check if below is solid (water on a surface should spread more)
                            const belowSolid = SOLID.has(this.w.gt(x, y + 1)) ||
                                (LIQUID.has(this.w.gt(x, y + 1)) && this.w.gll(x, y + 1) >= MAX_LIQUID - MIN_LIQUID);
                            const rate = belowSolid ? SIDE_FLOW_RATE * 2.0 : SIDE_FLOW_RATE;
                            let flow = Math.min(diff * rate * flowMult, diff * 0.25);
                            flow = Math.min(flow, remaining - MIN_LIQUID);

                            if (flow > MIN_LIQUID * 0.5) {
                                remaining -= flow;
                                if (side === T.AIR || NONSOLID_PASS.has(side)) {
                                    if (NONSOLID_PASS.has(side)) this.w.st(nx, y, t);
                                    else this.w.st(nx, y, t);
                                }
                                this.w.sll(nx, y, sideLvl + flow);
                                this.unsettle(nx, y);
                                didFlow = true;
                            }
                        }
                    }
                }

                // Update current cell
                if (remaining < MIN_LIQUID) {
                    this.w.st(x, y, T.AIR);
                    this.w.sll(x, y, 0);
                    didFlow = true;
                } else {
                    this.w.sll(x, y, remaining);
                }

                // Mark settled if no flow occurred and barely changed
                if (!didFlow && Math.abs(lvl - remaining) < MIN_LIQUID * 0.1) {
                    this.settled[idx] = 1;
                }
            }
        }
    }
}

// === GORE CHUNKS SYSTEM — Physics-based death fragments from mob texture ===
export class GoreChunk {
    constructor(x, y, vx, vy, imgData, chunkW, chunkH, srcCanvas) {
        this.x = x; this.y = y; this.vx = vx; this.vy = vy;
        this.imgData = imgData; this.chunkW = chunkW; this.chunkH = chunkH;
        this.rot = Math.random() * Math.PI * 2;
        this.rotSpd = (Math.random() - 0.5) * 0.12; // slower spin — heavy chunks
        this.life = 200 + Math.random() * 120; this.maxLife = this.life;
        this.bounceCount = 0;
        this.grounded = false;
        this.groundTimer = 0;
        // Create tiny canvas for this chunk
        this.cv = document.createElement('canvas');
        this.cv.width = chunkW; this.cv.height = chunkH;
        this.cv.getContext('2d').putImageData(imgData, 0, 0);
    }
    update(dt, world) {
        if (this.grounded) {
            this.groundTimer += dt;
            this.rotSpd *= 0.9;
            this.vx *= 0.8;
            this.rot += this.rotSpd;
            this.life -= dt / 20; // fade slower on ground
            return this.life > 0;
        }
        this.vy += 0.4; // heavy gravity
        this.vx *= 0.99;
        this.rot += this.rotSpd;
        let nx = this.x + this.vx, ny = this.y + this.vy;
        // Collision with solid tiles
        const tx = Math.floor(nx / TS), ty = Math.floor(ny / TS);
        if (tx >= 0 && tx < WW && ty >= 0 && ty < WH) {
            if (SOLID.has(world.gt(tx, ty))) {
                if (this.bounceCount < 3) {
                    // Bounce!
                    if (SOLID.has(world.gt(Math.floor(this.x / TS), ty))) {
                        this.vy *= -0.25; ny = this.y; // heavy bounce — low restitution
                        if (this.vy > -0.8 && this.vy < 0.8) {
                            this.grounded = true; this.vy = 0;
                        }
                    } else {
                        this.vx *= -0.25; nx = this.x; // heavy wall bounce
                    }
                    this.bounceCount++;
                    this.rotSpd *= 0.6;
                } else {
                    this.grounded = true; this.vy = 0; this.vx *= 0.3;
                }
            }
        }
        this.x = nx; this.y = ny;
        this.life -= dt / 16;
        return this.life > 0;
    }
    render(ctx) {
        const alpha = Math.min(1, this.life / (this.maxLife * 0.25));
        ctx.globalAlpha = Math.max(0, alpha);
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rot);
        ctx.drawImage(this.cv, -this.chunkW / 2, -this.chunkH / 2);
        ctx.restore();
        ctx.globalAlpha = 1;
    }
}

export class GoreSystem {
    constructor() { this.chunks = []; this.maxChunks = 200; this._offCv = document.createElement('canvas'); this._offCx = this._offCv.getContext('2d'); }
    spawnFromPlayer(player, renderFn, world) {
        // Render player onto offscreen canvas to capture texture
        const rw = PW + 16, rh = PH + 16;
        this._offCv.width = rw; this._offCv.height = rh;
        const oc = this._offCx;
        oc.clearRect(0, 0, rw, rh);
        // Draw player at canvas origin
        const ox = player.x, oy = player.y;
        const tempP = { ...player, x: 8, y: 8, dmgFlash: 0, squashX: 1, squashY: 1, swingTimer: 0 };
        // Manually draw player body (simplified) for gore capture
        const px = 8, py = 8;
        oc.fillStyle = '#3366ff'; oc.fillRect(px + 1, py + 6, 10, 11);
        oc.fillStyle = '#4488ff'; oc.fillRect(px + 2, py + 7, 8, 4);
        oc.fillStyle = '#1a3388'; oc.fillRect(px + 2, py + 16, 4, 8);
        oc.fillStyle = '#2244aa'; oc.fillRect(px + 6, py + 16, 4, 8);
        oc.fillStyle = '#ffcc88'; oc.fillRect(px + 2, py, 8, 7);
        oc.fillStyle = '#ffcc88'; oc.fillRect(px - 1, py + 7, 3, 7); oc.fillRect(px + 10, py + 7, 3, 7);
        oc.fillStyle = '#6a3010'; oc.fillRect(px + 1, py - 1, 10, 3);
        oc.fillStyle = '#664422'; oc.fillRect(px + 1, py + 15, 10, 2);
        oc.fillStyle = '#1a1a44'; oc.fillRect(px + 1, py + 23, 5, 1); oc.fillRect(px + 6, py + 23, 5, 1);
        // Break into gore chunks
        const chunkSize = 6;
        const numX = Math.ceil(rw / chunkSize), numY = Math.ceil(rh / chunkSize);
        const centerX = ox + PW / 2, centerY = oy + PH / 2;
        for (let cx2 = 0; cx2 < numX; cx2++) {
            for (let cy2 = 0; cy2 < numY; cy2++) {
                if (this.chunks.length >= this.maxChunks) return;
                const sx = cx2 * chunkSize, sy = cy2 * chunkSize;
                const cw = Math.min(chunkSize, rw - sx), ch = Math.min(chunkSize, rh - sy);
                if (cw <= 0 || ch <= 0) continue;
                const imgData = oc.getImageData(sx, sy, cw, ch);
                let hasPixels = false;
                for (let i = 3; i < imgData.data.length; i += 4) { if (imgData.data[i] > 10) { hasPixels = true; break; } }
                if (!hasPixels) continue;
                const worldX = ox - 8 + sx + cw / 2;
                const worldY = oy - 8 + sy + ch / 2;
                const ang = Math.atan2(worldY - centerY, worldX - centerX) + (Math.random() - 0.5) * 0.8;
                const spd = 2 + Math.random() * 4;
                this.chunks.push(new GoreChunk(
                    worldX, worldY,
                    Math.cos(ang) * spd + (Math.random() - 0.5) * 1.5,
                    Math.sin(ang) * spd - 2.5 - Math.random() * 2,
                    imgData, cw, ch
                ));
            }
        }
    }
    spawnFromMob(mob, world, time) {
        // Render mob onto offscreen canvas to capture its texture
        const pad = 8; // extra space for arms/wings that extend beyond hitbox
        const rw = mob.w + pad * 2, rh = mob.h + pad * 2;
        this._offCv.width = rw; this._offCv.height = rh;
        const oc = this._offCx;
        oc.clearRect(0, 0, rw, rh);
        // Temporarily override mob position to render at canvas origin
        const ox = mob.x, oy = mob.y;
        mob.x = pad; mob.y = pad;
        const oSquash = mob.squash, oStretch = mob.stretch;
        mob.squash = 1; mob.stretch = 1;
        MobRenderer.draw(oc, mob, time);
        mob.x = ox; mob.y = oy;
        mob.squash = oSquash; mob.stretch = oStretch;
        // Sample the rendered mob and break into LARGE chunks
        const chunkSize = Math.max(8, Math.min(14, Math.floor(Math.max(mob.w, mob.h) / 2)));
        const numX = Math.ceil(rw / chunkSize), numY = Math.ceil(rh / chunkSize);
        const centerX = ox + mob.w / 2, centerY = oy + mob.h / 2;
        let spawned = 0;
        for (let cx = 0; cx < numX; cx++) {
            for (let cy = 0; cy < numY; cy++) {
                if (this.chunks.length >= this.maxChunks) return;
                const sx = cx * chunkSize, sy = cy * chunkSize;
                const cw = Math.min(chunkSize, rw - sx), ch = Math.min(chunkSize, rh - sy);
                if (cw <= 0 || ch <= 0) continue;
                const imgData = oc.getImageData(sx, sy, cw, ch);
                // Check if chunk has any non-transparent pixels
                let hasPixels = false;
                for (let i = 3; i < imgData.data.length; i += 4) { if (imgData.data[i] > 10) { hasPixels = true; break; } }
                if (!hasPixels) continue;
                const worldX = ox - pad + sx + cw / 2;
                const worldY = oy - pad + sy + ch / 2;
                const ang = Math.atan2(worldY - centerY, worldX - centerX) + (Math.random() - 0.5) * 0.6;
                const spd = 1.5 + Math.random() * 3; // slower launch — heavy chunks
                this.chunks.push(new GoreChunk(
                    worldX, worldY,
                    Math.cos(ang) * spd + (Math.random() - 0.5) * 1.2,
                    Math.sin(ang) * spd - 1.5 - Math.random() * 2, // less upward launch
                    imgData, cw, ch
                ));
                spawned++;
            }
        }
    }
    update(dt, world) {
        for (let i = this.chunks.length - 1; i >= 0; i--) {
            if (!this.chunks[i].update(dt, world)) this.chunks.splice(i, 1);
        }
    }
    render(ctx) {
        for (const c of this.chunks) c.render(ctx);
    }
}

// === DAMAGE NUMBERS — Juicy with scaling and bounce ===
export class DmgNumbers {
    constructor() { this.nums = []; }
    add(x, y, val, col) {
        const isString = typeof val === 'string';
        const isBig = isString || val >= 20;
        this.nums.push({
            x: x + (Math.random() - 0.5) * 8, y, val, col: col || '#fff', life: 1, vy: -3.5,
            scale: isBig ? 1.8 : 1.2, targetScale: isBig ? 1.1 : 0.9, isBig
        });
    }

    update(dt) {
        for (let i = this.nums.length - 1; i >= 0; i--) {
            const n = this.nums[i];
            n.y += n.vy * dt / 16; n.vy *= 0.94; n.vy += 0.03; // slight gravity
            n.life -= dt / 900;
            n.scale += (n.targetScale - n.scale) * 0.15; // spring to target
            if (n.life <= 0) this.nums.splice(i, 1);
        }
    }
    render(ctx) {
        for (const n of this.nums) {
            const a = Math.max(0, n.life);
            ctx.globalAlpha = a;
            // Shadow for readability
            const fontSize = Math.round((n.isBig ? 13 : 10) * n.scale);
            ctx.font = `bold ${fontSize}px monospace`;
            // Outline
            ctx.fillStyle = 'rgba(0,0,0,0.6)';
            ctx.fillText(n.val, n.x + 1, n.y + 1);
            // Main color
            ctx.fillStyle = n.col;
            ctx.fillText(n.val, n.x, n.y);
        }
        ctx.globalAlpha = 1;
    }
}

// === XP ORB SYSTEM ===
// === ITEM DROP — physical items on the ground ===
export class ItemDrop {
    constructor(x, y, tileType, count) {
        this.x = x; this.y = y;
        this.t = tileType; this.c = count;
        this.vx = (Math.random() - 0.5) * 3;
        this.vy = -1 - Math.random() * 1.5; // gentle pop, not high launch
        this.w = 10; this.h = 10;
        this.ground = false;
        this.life = 30000;
        this.elapsed = 0;
        this.collected = false;
        this.bobPhase = Math.random() * Math.PI * 2;
        this.sparkTimer = 0;
        this.magnetDist = 40;
        this.magnetSpeed = 12;
        this._needsUnstick = true; // check on first update
    }
    update(dt, px, py, world) {
        this.elapsed += dt;
        if (this.elapsed > this.life) { this.collected = true; return; }

        // Unstick from solid blocks on first frames
        if (this._needsUnstick && world) {
            this._needsUnstick = false;
            const SOLID_Q = (tx, ty) => {
                const t = world.gt(tx, ty);
                return ItemDrop._SOLID ? ItemDrop._SOLID.has(t) : (t > 0 && t !== 8 && t !== 32 && t !== 51);
            };
            const cx = Math.floor((this.x + this.w / 2) / 16);
            const cy = Math.floor((this.y + this.h / 2) / 16);
            if (SOLID_Q(cx, cy)) {
                // Push down until free
                for (let dy = 1; dy < 8; dy++) {
                    if (!SOLID_Q(cx, cy + dy)) {
                        this.y = (cy + dy) * 16;
                        this.vy = 0;
                        break;
                    }
                }
            }
        }

        // Gravity & physics
        if (!this.ground) {
            this.vy += 0.35;
            this.vx *= 0.98;
        } else {
            this.vx *= 0.85;
            if (Math.abs(this.vx) < 0.1) this.vx = 0;
        }

        // Move X
        const nx = this.x + this.vx;
        const txL = Math.floor(nx / 16), txR = Math.floor((nx + this.w) / 16);
        const tyM = Math.floor((this.y + this.h / 2) / 16);
        const SOLID_CHECK = (tx, ty) => {
            if (!world) return false;
            const t = world.gt(tx, ty);
            return ItemDrop._SOLID ? ItemDrop._SOLID.has(t) : (t > 0 && t !== 8 && t !== 32 && t !== 51);
        };
        if (!SOLID_CHECK(txL, tyM) && !SOLID_CHECK(txR, tyM)) {
            this.x = nx;
        } else {
            this.vx = -this.vx * 0.3;
        }

        // Move Y
        const ny = this.y + this.vy;
        const byBottom = Math.floor((ny + this.h) / 16);
        const bxM = Math.floor((this.x + this.w / 2) / 16);
        if (!SOLID_CHECK(bxM, byBottom)) {
            this.y = ny;
            this.ground = false;
        } else {
            // Snap to surface
            this.y = byBottom * 16 - this.h;
            this.vy = 0;
            this.ground = true;
        }

        // Magnet pickup — accelerating snap when close
        const dx = px - (this.x + this.w / 2);
        const dy = py - (this.y + this.h / 2);
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < this.magnetDist) {
            this._magnetizing = true;
            if (dist < 8) {
                this.collected = true;
                return;
            }
            // Accelerating approach — starts slow, gets fast
            const t = 1 - dist / this.magnetDist; // 0 at edge, 1 at center
            const speed = 3 + t * 12;
            this.x += (dx / dist) * speed;
            this.y += (dy / dist) * speed;
            this.vx = 0; this.vy = 0;
            this.ground = false;
        } else {
            this._magnetizing = false;
        }

        this.sparkTimer += dt;
    }
    render(ctx, time) {
        const bob = this.ground ? Math.sin(time / 500 + this.bobPhase) * 2 : 0;
        const drawX = this.x;
        const drawY = this.y + bob;
        const fadeIn = Math.min(1, this.elapsed / 200);
        const fadeOut = this.elapsed > this.life - 3000 ? Math.max(0.2, (this.life - this.elapsed) / 3000) : 1;
        const alpha = fadeIn * fadeOut;

        // Glow when being magnetized
        if (this._magnetizing) {
            ctx.globalAlpha = alpha * 0.3;
            ctx.fillStyle = ItemDrop._TC ? (ItemDrop._TC[this.t] || '#fff') : '#fff';
            ctx.fillRect(drawX - 2, drawY - 2, this.w + 4, this.h + 4);
        }

        // Small shadow under item
        ctx.globalAlpha = alpha * 0.15;
        ctx.fillStyle = '#000';
        ctx.fillRect(drawX + 2, drawY + this.h, this.w - 4, 1);

        // Render the item icon
        ctx.globalAlpha = alpha;
        ItemDrop.renderIcon(ctx, this.t, drawX, drawY, this.w, this.h);

        // Count badge
        if (this.c > 1) {
            ctx.globalAlpha = alpha;
            ctx.font = 'bold 7px monospace';
            ctx.textAlign = 'center';
            ctx.fillStyle = '#000';
            ctx.fillText(this.c, drawX + this.w / 2 + 1, drawY + this.h + 1);
            ctx.fillStyle = '#fff';
            ctx.fillText(this.c, drawX + this.w / 2, drawY + this.h);
            ctx.textAlign = 'left';
        }

        // Sparkle
        if (this.ground && this.sparkTimer > 800 && Math.random() > 0.96) {
            this.sparkTimer = 0;
            // rendered by game particle system
        }
        ctx.globalAlpha = 1;
    }

    // Static icon renderer — used by inventory, hotbar, drops
    static _iconCache = {};
    static renderIcon(ctx, tileType, x, y, w, h) {
        // Try cached canvas
        const key = tileType;
        if (!ItemDrop._iconCache[key]) {
            ItemDrop._iconCache[key] = ItemDrop._createIcon(tileType);
        }
        const icon = ItemDrop._iconCache[key];
        if (icon) {
            ctx.drawImage(icon, x, y, w, h);
        }
    }
    static _createIcon(t) {
        const cv = document.createElement('canvas');
        cv.width = 16; cv.height = 16;
        const cx = cv.getContext('2d');
        // Import TC colors
        const TC = ItemDrop._TC;
        if (!TC) return null;
        const c = TC[t];
        if (!c) return null;

        const shade = (hex, amt) => {
            if (!hex || hex[0] !== '#') return hex;
            let r = parseInt(hex.substr(1, 2), 16) + amt;
            let g = parseInt(hex.substr(3, 2), 16) + amt;
            let b = parseInt(hex.substr(5, 2), 16) + amt;
            return `rgb(${Math.max(0, Math.min(255, r))},${Math.max(0, Math.min(255, g))},${Math.max(0, Math.min(255, b))})`;
        };
        const dk = shade(c, -25);
        const lt = shade(c, 30);

        // Categorized icon drawing
        // Ores & gems — diamond shapes
        const ORES = [13, 14, 15, 16, 72, 73, 74, 75, 76, 77, 78, 33, 53, 198]; // COAL..LUMINITE
        // Tools/special — drawn as tool shapes
        const TORCHES = [21, 81, 84]; // TORCH, DTORCH, LANTERN
        const FLOWERS = [23, 24, 143, 144, 145]; // flowers
        const MUSHROOMS = [22, 43]; // MUSHROOM, GLOWMUSH

        if (ORES.includes(t)) {
            // Ore chunk icon
            cx.fillStyle = '#666'; cx.fillRect(2, 4, 12, 10); // stone base
            cx.fillStyle = '#555'; cx.fillRect(2, 12, 12, 2);
            cx.fillStyle = '#777'; cx.fillRect(2, 4, 12, 2);
            // Ore veins
            cx.fillStyle = c;
            cx.fillRect(4, 6, 4, 3); cx.fillRect(9, 8, 3, 4);
            cx.fillRect(5, 10, 3, 2);
            cx.fillStyle = lt;
            cx.fillRect(4, 6, 2, 1); cx.fillRect(9, 8, 1, 1);
            cx.fillStyle = dk;
            cx.fillRect(7, 8, 1, 1); cx.fillRect(11, 11, 1, 1);
        } else if (TORCHES.includes(t)) {
            // Torch icon
            cx.fillStyle = '#8B5E3C'; cx.fillRect(7, 5, 2, 9); // stick
            cx.fillStyle = c; cx.fillRect(5, 1, 6, 5); // flame
            cx.fillStyle = lt; cx.fillRect(6, 2, 4, 3);
            cx.fillStyle = '#fff'; cx.fillRect(7, 2, 2, 2);
        } else if (FLOWERS.includes(t)) {
            // Flower icon
            cx.fillStyle = '#3a7a2a'; cx.fillRect(7, 8, 2, 6); // stem
            cx.fillStyle = c;
            cx.fillRect(5, 3, 6, 5); // petals
            cx.fillStyle = '#ffee44'; cx.fillRect(7, 4, 2, 2); // center
        } else if (MUSHROOMS.includes(t)) {
            // Mushroom icon
            cx.fillStyle = '#d0c0a0'; cx.fillRect(6, 8, 4, 6); // stem
            cx.fillStyle = c; cx.fillRect(3, 2, 10, 7); // cap
            cx.fillStyle = lt; cx.fillRect(3, 2, 10, 2);
            cx.fillStyle = '#fff'; cx.fillRect(5, 4, 2, 2); cx.fillRect(9, 5, 2, 1); // spots
        } else if (t === 9) { // WOOD
            // Log icon
            cx.fillStyle = c; cx.fillRect(2, 3, 12, 10);
            cx.fillStyle = dk; cx.fillRect(2, 11, 12, 2);
            cx.fillStyle = lt; cx.fillRect(2, 3, 12, 2);
            // Rings
            cx.fillStyle = shade(c, -10);
            cx.fillRect(5, 6, 6, 1); cx.fillRect(4, 8, 8, 1);
        } else if (t === 10) { // LEAVES
            cx.fillStyle = c;
            cx.fillRect(2, 2, 12, 12);
            cx.fillStyle = lt;
            cx.fillRect(3, 2, 3, 3); cx.fillRect(8, 5, 4, 3);
            cx.fillStyle = dk;
            cx.fillRect(2, 12, 12, 2);
        } else if (t === 20) { // LADDER
            cx.fillStyle = '#c8a050';
            cx.fillRect(3, 1, 2, 14); cx.fillRect(11, 1, 2, 14); // rails
            cx.fillRect(5, 3, 6, 2); cx.fillRect(5, 7, 6, 2); cx.fillRect(5, 11, 6, 2); // rungs
        } else if (t === 26) { // CACTUS
            cx.fillStyle = c; cx.fillRect(5, 2, 6, 12);
            cx.fillStyle = lt; cx.fillRect(5, 2, 2, 12);
            cx.fillStyle = dk;
            cx.fillRect(2, 5, 3, 3); cx.fillRect(11, 8, 3, 3); // arms
            cx.fillStyle = shade(c, 10);
            cx.fillRect(2, 5, 3, 1); cx.fillRect(11, 8, 3, 1);
        } else {
            // Generic block icon — 3D cube look
            cx.fillStyle = c; cx.fillRect(1, 1, 14, 14);
            // Top highlight
            cx.fillStyle = lt; cx.fillRect(1, 1, 14, 3);
            cx.fillRect(1, 1, 3, 14);
            // Bottom/right shadow
            cx.fillStyle = dk; cx.fillRect(1, 12, 14, 3);
            cx.fillRect(12, 1, 3, 14);
            // Inner detail
            cx.fillStyle = shade(c, -8);
            cx.fillRect(4, 5, 3, 2); cx.fillRect(9, 8, 3, 2);
            cx.fillRect(5, 10, 4, 2);
        }
        return cv;
    }
    static _TC = null; // Set by Game on init
    static _SOLID = null; // Set by Game on init
}

export class XpOrb {
    constructor(x, y, val, px, py) {
        this.x = x; this.y = y; this.val = val;
        this.tx = px; this.ty = py; // target (player pos)
        const ang = Math.random() * Math.PI * 2;
        const spd = 2 + Math.random() * 3;
        this.vx = Math.cos(ang) * spd;
        this.vy = Math.sin(ang) * spd - 2;
        this.life = 1; this.phase = 0; // 0=burst, 1=home
        this.burstTime = 200 + Math.random() * 150;
        this.elapsed = 0;
        this.sz = 1.5 + Math.min(val / 8, 2);
        this.trail = [];
        this.collected = false;
        // Color based on value
        if (val >= 50) { this.col = '#ffd700'; this.glow = '#ffee88'; } // gold
        else if (val >= 10) { this.col = '#44ff88'; this.glow = '#88ffbb'; } // bright green
        else { this.col = '#33dd66'; this.glow = '#66ff99'; } // green
    }
    update(dt, px, py) {
        this.tx = px; this.ty = py;
        this.elapsed += dt;
        // Trail
        if (this.elapsed % 2 < 1) this.trail.push({ x: this.x, y: this.y, a: 0.6 });
        if (this.trail.length > 8) this.trail.shift();
        for (const t of this.trail) t.a *= 0.88;
        if (this.phase === 0) {
            // Burst outward with gravity
            this.vy += 0.12;
            this.vx *= 0.97;
            this.x += this.vx; this.y += this.vy;
            if (this.elapsed > this.burstTime) this.phase = 1;
        } else {
            // Home toward player with acceleration
            const dx = this.tx - this.x, dy = this.ty - this.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < 12) { this.collected = true; return; }
            const accel = 0.4 + (this.elapsed - this.burstTime) * 0.003;
            this.vx += (dx / dist) * accel;
            this.vy += (dy / dist) * accel;
            const maxSpd = 8 + (this.elapsed - this.burstTime) * 0.01;
            const curSpd = Math.sqrt(this.vx * this.vx + this.vy * this.vy);
            if (curSpd > maxSpd) { this.vx *= maxSpd / curSpd; this.vy *= maxSpd / curSpd; }
            this.x += this.vx; this.y += this.vy;
        }
        this.life = Math.max(0, 1 - this.elapsed / 3000);
    }
    render(ctx, time) {
        // Trail
        for (const t of this.trail) {
            if (t.a < 0.05) continue;
            ctx.globalAlpha = t.a * 0.4;
            ctx.fillStyle = this.glow;
            ctx.fillRect(t.x - 1.5, t.y - 1.5, 3, 3);
        }
        const pulse = 1 + Math.sin(time * 0.008 + this.elapsed) * 0.15;
        const sz = this.sz * pulse;
        // Outer glow
        ctx.globalAlpha = 0.2 * this.life;
        ctx.fillStyle = this.glow;
        ctx.beginPath(); ctx.arc(this.x, this.y, sz * 1.8, 0, Math.PI * 2); ctx.fill();
        // Core
        ctx.globalAlpha = 0.85 * this.life;
        ctx.fillStyle = this.col;
        ctx.beginPath(); ctx.arc(this.x, this.y, sz, 0, Math.PI * 2); ctx.fill();
        // Inner bright spot
        ctx.globalAlpha = 0.5 * this.life;
        ctx.fillStyle = '#fff';
        ctx.beginPath(); ctx.arc(this.x - sz * 0.2, this.y - sz * 0.2, sz * 0.3, 0, Math.PI * 2); ctx.fill();
        ctx.globalAlpha = 1;
    }
}